import { useEffect, useRef, useState } from 'react';
import { ReferenceValue } from '../types/types';

/**
 * Chips shrink when their total width exceeds (availableWidth - SHRINK_BUFFER).
 * Use a positive value so chips switch to compact style slightly before a horizontal
 * scrollbar would appear, avoiding overlap with the scrollbar.
 */
const SHRINK_BUFFER = 30;
/**
 * Chips return to normal size when their total width is at or below (availableWidth - ENLARGE_BUFFER).
 * Use a value noticeably larger than SHRINK_BUFFER so chips grow back only after the scrollbar
 * has been gone for a while. The gap (ENLARGE_BUFFER - SHRINK_BUFFER) must be at least the
 * typical difference between normal and compact chip total widths, otherwise the component
 * can flip between states in a loop.
 */
const ENLARGE_BUFFER = 4 * SHRINK_BUFFER;

const useChipsScrollbarDetection = (
  value: ReferenceValue,
  scrollContainerSelector: string,
  chipSelector: string
) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

  useEffect(() => {
    // if (!isMultiselect) return;
    const container = containerRef.current;
    if (!container) return;

    const scrollContainerEl = container.querySelector<HTMLElement>(
      scrollContainerSelector
    );
    if (!scrollContainerEl) return;

    const check = () => {
      const chips =
        container?.querySelectorAll<HTMLElement>(chipSelector) ?? [];

      let chipsTotalWidth = 0;
      chips.forEach((chip) => {
        const style = window.getComputedStyle(chip);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        chipsTotalWidth += chip.offsetWidth + marginLeft + marginRight;
      });

      const availableWidth = scrollContainerEl.clientWidth;

      if (chipsTotalWidth > availableWidth - SHRINK_BUFFER) {
        setHasHorizontalScroll(true);
      } else if (chipsTotalWidth <= availableWidth - ENLARGE_BUFFER) {
        setHasHorizontalScroll(false);
      }
    };

    check();
    const observer = new ResizeObserver(() => requestAnimationFrame(check));
    observer.observe(scrollContainerEl);

    return () => {
      observer.disconnect();
    };
  }, [value]);

  return {
    containerRef,
    hasHorizontalScroll,
  };
};

export default useChipsScrollbarDetection;
